// Assistant authentication hooks using React Query (same pattern as staff)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import { EMPTY_PERMISSIONS } from '@/lib/permissions'
import type { Assistant, AssistantPermissions } from '@/types/entities'

const ASSISTANT_STORAGE_KEY = 'digimataam_assistant_auth'
const ASSISTANT_RESTAURANT_KEY = 'digimataam_assistant_restaurant'

function getCachedAssistant(): Assistant | null {
  try {
    const raw = localStorage.getItem(ASSISTANT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Assistant>
    // Minimal shape check
    if (
      typeof parsed !== 'object' ||
      !parsed ||
      typeof parsed.id !== 'string' ||
      typeof parsed.restaurantId !== 'string' ||
      typeof parsed.username !== 'string'
    ) {
      return null
    }
    return parsed as Assistant
  } catch {
    return null
  }
}

/**
 * Get current authenticated assistant
 * Uses React Query for automatic caching and retry logic
 */
export function useCurrentAssistant() {
  return useQuery({
    queryKey: queryKeys.auth.assistant(),
    initialData: () => getCachedAssistant(),
    queryFn: async (): Promise<Assistant | null> => {
      const cached = getCachedAssistant()

      // Prefer session (local) over getUser() (network) to avoid hanging on reload.
      const timeoutMs = 8000

      let authUserId: string | null = null
      try {
        // getSession is local; keep it simple and fast
        const { data } = await supabase.auth.getSession()
        authUserId = data.session?.user?.id ?? null
      } catch {
        // If auth is stuck recovering, fall back to cached assistant so dashboards don't spin forever
        return cached
      }

      if (!authUserId) {
        return cached
      }

      // Fetch assistant profile from database
      let assistantResult: { data: any; error: any }
      try {
        const controller = new AbortController()
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
        try {
          // Supabase supports abortSignal at runtime, but our TS types don't expose it here.
          assistantResult = await (supabase
            .from('assistants')
            .select('*')
            .eq('id', authUserId)
            .maybeSingle() as any).abortSignal(controller.signal)
        } finally {
          window.clearTimeout(timeoutId)
        }
      } catch {
        return cached
      }

      const { data: assistantData, error: assistantError } = assistantResult

      if (assistantError) {
        console.error('[useCurrentAssistant] Database error:', assistantError)
        // Non-destructive fallback: avoid signing out on transient reload issues
        return cached
      }

      if (!assistantData) {
        console.warn('[useCurrentAssistant] Assistant profile not found:', authUserId)
        return cached
      }

      // Check if assistant is active
      if (!assistantData.is_active || assistantData.deleted_at) {
        console.warn('[useCurrentAssistant] Assistant is inactive or deleted:', authUserId)
        localStorage.removeItem(ASSISTANT_STORAGE_KEY)
        return null
      }

      // Map to Assistant entity
      const assistant: Assistant = {
        id: assistantData.id,
        username: assistantData.username,
        email: assistantData.email,
        roleName: assistantData.role_name || '',
        permissions: (assistantData.permissions as AssistantPermissions) || EMPTY_PERMISSIONS,
        restaurantId: assistantData.restaurant_id,
        isActive: assistantData.is_active ?? true,
        languagePreference: assistantData.language_preference || 'ar',
        deletedAt: assistantData.deleted_at,
        createdAt: assistantData.created_at ?? new Date().toISOString(),
        updatedAt: assistantData.updated_at ?? new Date().toISOString(),
      }

      // Save to localStorage for route guards
      localStorage.setItem(ASSISTANT_STORAGE_KEY, JSON.stringify(assistant))

      return assistant
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

/**
 * Assistant login mutation
 */
export function useAssistantLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      username,
      password,
      restaurantId,
    }: {
      username: string
      password: string
      restaurantId: string
    }) => {
      // Get email from server-side function
      const { data: email, error: emailError } = await supabase.rpc(
        'get_assistant_login_email' as any,
        {
          p_username: username,
          p_restaurant_id: restaurantId,
        }
      )

      if (emailError || !email) {
        throw new Error('Assistant not found or inactive')
      }

      // Sign in with Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email: String(email),
        password,
      })

      if (error) {
        throw error
      }

      // Store restaurant ID for redirect
      localStorage.setItem(ASSISTANT_RESTAURANT_KEY, restaurantId)

      return data
    },
    onSuccess: () => {
      // Invalidate queries to refetch assistant data
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.assistant() })
    },
  })
}

/**
 * Assistant logout mutation
 */
export function useAssistantLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },
    onSuccess: () => {
      // Clear assistant data from localStorage
      localStorage.removeItem(ASSISTANT_STORAGE_KEY)
      // Clear all queries
      queryClient.clear()
    },
  })
}

/**
 * Check if user is authenticated assistant
 */
export function useIsAssistant() {
  const { data: assistant, isLoading } = useCurrentAssistant()
  return { isAssistant: !!assistant, isLoading }
}

/**
 * Get assistant redirect URL based on stored restaurant ID
 */
export function getAssistantRedirectUrl(): string {
  const restaurantId = localStorage.getItem(ASSISTANT_RESTAURANT_KEY)
  if (restaurantId) {
    return `/assistant-login?restaurant=${restaurantId}`
  }
  return '/'
}

/**
 * Permission helper hooks
 */
export function useAssistantPermission(
  page: keyof AssistantPermissions,
  action: string
): boolean {
  const { data: assistant } = useCurrentAssistant()
  if (!assistant?.permissions || !assistant.permissions[page]) return false
  const pagePermissions = assistant.permissions[page] as Record<string, boolean>
  return pagePermissions[action] === true
}

export function useCanViewPage(page: keyof AssistantPermissions): boolean {
  const { data: assistant } = useCurrentAssistant()
  if (!assistant?.permissions || !assistant.permissions[page]) return false
  const pagePermissions = assistant.permissions[page] as Record<string, boolean>
  return Object.values(pagePermissions).some((val) => val === true)
}
